// src/components/registry/ServerCard.tsx
//
// Renders one MCP server catalog entry as a card: its display name, description,
// Category label, a serverKind badge (remote-http / local-command), and an
// optional homepage link.
//
// For SUPPORTED entries (isSupportedServerKind), it embeds the entry-driven
// IdeInstallMenu so the developer can install the server into their IDE. For an
// UNSUPPORTED serverKind, it renders an explanatory indication and omits the
// install affordance entirely (Requirement 8.4).

import { useEffect, useState } from 'react'
import { ExternalLink, ChevronDown, ChevronRight } from 'lucide-react'
import { Card } from '../ui/Card'
import { IdeInstallMenu } from '../install/IdeInstallMenu'
import { isSupportedServerKind, type CatalogEntry } from '../../lib/catalog'
import { resolveMcpBaseUrl } from '../../lib/mcpConfig'

interface ServerCardProps {
  entry: CatalogEntry
  baseUrl?: string
  defaultExpanded?: boolean
}

/** A small pill badge displaying the entry's serverKind. */
function ServerKindBadge({ serverKind }: Readonly<{ serverKind: string }>) {
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 'var(--radius-s)',
        background: 'var(--surface-hover)',
        border: '1px solid var(--border)',
        color: 'var(--text-secondary)',
        fontFamily: 'var(--font-mono, monospace)',
      }}
    >
      {serverKind}
    </span>
  )
}

export function ServerCard({ entry, baseUrl = resolveMcpBaseUrl(), defaultExpanded = false }: Readonly<ServerCardProps>) {
  const supported = isSupportedServerKind(entry)
  const isHashTarget = typeof window !== 'undefined' && window.location.hash === `#${entry.id}`
  const [expanded, setExpanded] = useState(defaultExpanded || isHashTarget)

  useEffect(() => {
    if (!isHashTarget) return
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(entry.id)?.scrollIntoView?.({ block: 'start' })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [entry.id, isHashTarget])

  return (
    <div id={entry.id}>
      <Card>
        <div
          onClick={() => setExpanded(!expanded)}
          style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-2)', cursor: 'pointer' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {expanded ? <ChevronDown size={16} color="var(--text-muted)" /> : <ChevronRight size={16} color="var(--text-muted)" />}
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>{entry.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{entry.category}</div>
            </div>
          </div>
          <ServerKindBadge serverKind={entry.serverKind} />
        </div>

        {expanded && (
          <>
            {entry.description && (
              <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 'var(--space-2)' }}>
                {entry.description}
              </div>
            )}

            {entry.homepage && (
              <div style={{ marginTop: 'var(--space-2)' }}>
                <a
                  href={entry.homepage}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--brand)', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Homepage <ExternalLink size={12} />
                </a>
              </div>
            )}

            <div style={{ marginTop: 'var(--space-3)' }}>
              {supported ? (
                <IdeInstallMenu entry={entry} baseUrl={baseUrl} />
              ) : (
                <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  This server type isn&apos;t supported for one-click install yet.
                </div>
              )}
            </div>
          </>
        )}
      </Card>
    </div>
  )
}
